import { IconX } from "@tabler/icons-react";
import React, { useEffect, useState } from "react";
import CustomFileInput from "../../../components/common/CustomFileInput";
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
} from "react-hook-form";
import { useDriverUpdateMutation } from "../../../apis/driverApis";
import toast from "react-hot-toast";
import Input from "../../../components/Input";
import formatDate from "../../../utilities/helper";
import SelectInput from "../../../components/Select";
import {
  useFetchCountriesAllData,
  useFetchCountryCitiesAllData,
} from "../../../apis/locationApis";
import { useFetchOutSourcesData } from "../../../apis/outSourceApis";
import { T, useLanguage } from "../../../context/LanguageContext";

const driverTypes = [
  { id: 1, value: "COURIER", text: "Courier" },
  { id: 2, value: "FM", text: "FM" },
  { id: 3, value: "BOTH", text: "Both" },
  { id: 4, value: "DARK_STORE", text: "Dark Store" },
  { id: 5, value: "OUTSOURCE", text: "Outsource" },
  { id: 6, value: "IN_HOUSE", text: "In House" },
];

type ClientData = {
  driverName: string;
  mobileNumber: string;
  emailId: string;
  country: string;
  city: string;
  licenseNumber: string;
  licenseExpiryDate: string;
  photo: string | File;
  licenseImage: string | File;
};

const initialData: ClientData = {
  driverName: "Jhone",
  mobileNumber: "9856321470",
  emailId: "example@gmail.com",
  country: "India",
  city: "Jaipur",
  licenseNumber: "8791321654981",
  licenseExpiryDate: "25 Aug 2035",
  photo: "/img/dummy-driver.png",
  licenseImage: "/img/dummy-license.png",
};

type FormValues = {
  name: string;
  mobileNo: string;
  email: string;
  licenseNumber: string;
  licenseExpiry: string;
  countryId: string;
  cityId: string;
  image: string;
  document: string;
  status?: string;
  districtId: string;
  warehouseId: string;
  driverType?: string;
  driverCode?: string;
  outsourceId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  getValues: () => any;
  register: UseFormRegister<FormValues>;
  errors: FieldErrors<FormValues>;
  clearErrors?: UseFormClearErrors<FormValues>;
  editSubmit: (
    callback: (data: FormValues) => void
  ) => (e?: React.BaseSyntheticEvent) => void;
  reset: (data?: any) => void;
  refetch: () => void;
  editSetValue: any;
};


const EditViewDriverModal = ({
  open,
  onClose,
  getValues,
  register,
  errors,
  clearErrors,
  editSetValue,
  editSubmit,
  reset,
  refetch,
}: Props) => {
  const { t } = useLanguage();
  const [editMode, setEditMode] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<any>();
  const [selectedCity, setSelectedCity] = useState<any>();
  const { data } = useFetchCountriesAllData();
  const { data: cities, refetch: fetchData } = useFetchCountryCitiesAllData(
    selectedCountry?.id
  );
  const values = getValues();
  const { mutate, isPending } = useDriverUpdateMutation(values.id);
  const { data: outSourcesList } = useFetchOutSourcesData(1, 100, "");

  
  useEffect(() => {
    if (selectedCountry) {
      fetchData();
    }
  }, [selectedCountry]);
  if (!open) return null;

  const toggleMode = () => {
    if (!editMode) reset(values);
    setEditMode(!editMode);
  };

  const handleUpdate = editSubmit((data: FormValues) => {
    let { ...otherData } = data;
    mutate(otherData, {
      onSuccess: () => {
        refetch();
        setEditMode(false);
      },
      onError: (error: any) => {
        toast.error(t("Failed to update client."));
      },
    });
  });

  // useEffect(() => {
  //   if (selectedCity && selectedCountry) {
  //     getIds({
  //       cityId: selectedCity?.id,
  //       countryId: selectedCountry?.id,
  //     });
  //   }
  // }, [selectedCity, selectedCountry]);

  const getValidationRules = (key: string) => {
    let label = key.replace(/([A-Z])/g, " $1");
    label = label[0]?.toUpperCase() + label.slice(1);

    const commonRules = {
      required: `${label} is required`,
    };

    switch (key) {
      case "email":
        return {
          ...commonRules,
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address",
          },
        };
      case "name":
        return {
          ...commonRules,
          validate: (value: string) => {
            const trimmed = value.trim();
            if (trimmed.length < 3) return "Name must be at least 3 characters";
            if (trimmed.length > 30)
              return "Name must be at most 30 characters";
            return true;
          },
        };
      case "mobileNo":
        return {
          ...commonRules,
          validate: (value: string) => {
            const trimmed = value.trim();
            if (trimmed.length !== 10)
              return "Mobile number must be exactly 10 digits";
            return true;
          },
        };
      case "licenseNumber":
        return {
          ...commonRules,
          validate: (value: string) => {
            const trimmed = value.trim();
            if (trimmed.length !== 10)
              return "License number must be exactly 10 digits";
            return true;
          },
        };
      case "driverCode":
        return {
          ...commonRules,
          validate: (value: string) => {
            const trimmed = value.trim();
            if (trimmed.length < 3)
              return "Driver code must be at least 3 characters";
            if (trimmed.length > 10)
              return "Driver code must be at most 10 characters";
            if (!/^[A-Za-z0-9]+$/.test(trimmed)) {
              return "Driver code must be alphanumeric without spaces";
            }
            return true;
          },
        };
      case "driverType":
        return {
          ...commonRules,
        };
      default:
        return commonRules;
    }
  };

  if (!open) return null;

  return (
    <div className="fixed top-0 left-0 w-full h-screen bg-black/60 z-50 flex items-center justify-center">
      <div className="bg-[#F7F6F2] rounded-2xl w-[655px]">
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#d2d2d2]">
          <h1 className="text-2xl">
            {editMode ? <T>Edit Driver</T> : <T>View Driver</T>}
          </h1>
          <button
            onClick={() => {
              if (editMode) {
                reset(values);
                setEditMode(false);
              } else onClose();
            }}
            className="bg-[#F2F2F7] rounded-full p-1 cursor-pointer"
          >
            <IconX size={20} />
          </button>
        </div>

        <div className="w-full max-h-[520px] overflow-y-auto overflow-hidden p-4 divide-y divide-[#D2D2D2]">
          {Object.entries(values).map(([key, value]) => {
            if (["id", "createdAt"].includes(key)) return null;
            if (key === "outsourceId" && values.driverType !== "OUTSOURCE") return null;
            return (
              <div
                key={key}
                className="flex justify-between items-center w-full p-1.5"
              >
                <div className="w-full">
                  <label className="capitalize text-[#212121] text-sm font-medium">
                    <T>
                    {key === "image"
                      ? "Image"
                      : key === "document"
                      ? "Document"
                      : key === "driverType"
                      ? "Driver Type"
                      : key === "driverCode"
                      ? "Driver Code"
                      : key === "outsourceId"
                      ? "Outsource Company"
                      : key.replace(/([A-Z])/g, " $1")}
                    </T>
                  </label>
                </div>
                <div className="w-full">
                  {editMode ? (
                    key === "image" || key === "document" ? (
                      <CustomFileInput
                        onFileSelect={(file) => editSetValue(key, file)}
                        initialFile={(getValues as any)(key)}
                      />
                    ) : key === "country" ? (
                       <SelectInput
                                name="countryId"
                                label="Country"
                                placeholder="Select Country"
                                selectOptions={
                                  data?.map((country: any) => ({
                                    id: country?.id,
                                    value: country?.name,
                                    text: country?.name,
                                  })) || []
                                }
                                register={register}
                                clearErrors={clearErrors}
                                validation={{ required: "Country is required" }}
                                error={errors.countryId}
                                disabled={!data || data.length === 0}
                                value={selectedCountry}
                                setValue={(val: string) => {
                                  setSelectedCountry(data.find((e: any) => e?.name === val));
                                  setSelectedCity("");
                                }}
                              /> ) : key === "city" ? (
                              <SelectInput
                                name="cityId"
                                label="City"
                                placeholder="Select City"
                                selectOptions={
                                  cities?.map((city: any) => ({
                                    id: city?.id,
                                    value: city?.name,
                                    text: city?.name,
                                  })) || []
                                }
                                clearErrors={clearErrors}
                                register={register}
                                validation={{ required: "City is required" }}
                                error={errors?.cityId}
                                disabled={!cities || cities?.length === 0}
                                value={selectedCity}
                                setValue={(val: string) =>
                                  setSelectedCity(cities.find((e: any) => e?.name === val))
                                }
                              />
                    ) : key === "driverType" ? (
                      <SelectInput
                        name="driverType"
                        label="Driver Type"
                        placeholder="Select Driver Type"
                        selectOptions={driverTypes}
                        register={register}
                        clearErrors={clearErrors}
                        validation={getValidationRules(key)}
                        error={errors?.driverType}
                        value={value}
                        setValue={(val: string) => {
                          editSetValue(key, val);
                          if (val !== "OUTSOURCE") editSetValue("outsourceId", undefined as any);
                        }}
                      />
                    ) : key === "outsourceId" ? (
                      <SelectInput
                        name="outsourceId"
                        label="Select Outsource Company"
                        placeholder="Select Company"
                        selectOptions={
                          outSourcesList?.outSources?.map((os: any) => ({
                            id: os.id,
                            value: os.id,
                            text: os.companyName,
                          })) || []
                        }
                        register={register}
                        clearErrors={clearErrors}
                        validation={{ required: "Outsource Company is required" }}
                        error={errors?.outsourceId as any}
                        value={value}
                        setValue={(val: string) => editSetValue(key, val)}
                      />
                    ) : (
                      <Input
                        name={key}
                        type={
                          key === "mobileNo" || key === "licenseNumber"
                            ? "number"
                            : key === "licenseExpiry"
                            ? "date"
                            : "text"
                        }
                        title=""
                        placeholder={String(values[key as keyof FormValues])}
                        other={{
                          className:
                            "w-full space-y-1 px-4 py-2 text-sm rounded-full outline-none bg-white border border-[#D8D8D8]",
                        }}
                        register={register}
                        error={errors?.[key as keyof FormValues]}
                        validation={getValidationRules(key)}
                      />
                    )
                  ) : key === "image" || key === "document" ? (
                    <img
                      src={value as string}
                      alt={key}
                      className="h-20 w-20 rounded object-cover"
                    />
                  ) : typeof value === "string" ? (
                    <div className="text-[#1E293B]">
                      {key === "outsourceId"
                        ? outSourcesList?.outSources?.find((os: any) => os.id === value)?.companyName || value
                        : key === "licenseExpiry"
                        ? formatDate(value)
                        : value}
                    </div>
                  ) : (
                    <div className="text-[#1E293B]">{(value as File).name}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#D8D8D8] px-4 py-4 flex justify-end space-x-2">
          <button
            className="border border-[#D8D8D8] bg-transparent flex items-center rounded-full p-2 px-4 space-x-1 cursor-pointer text-base font-medium"
            onClick={() => {
              if (editMode) setEditMode(false);
              else onClose();
            }}
          >
            <T>Cancel</T>
          </button>
          <button
            className="border border-[#FBD037] bg-[#FBD037] flex items-center rounded-full p-2 px-6 space-x-1 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-sm font-medium"
            onClick={editMode ? handleUpdate : toggleMode}
          >
            {editMode ? <T>Update</T> : <T>Edit</T>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditViewDriverModal;
